import { useState } from "react";
import { api } from "../../api";
import type { SpecRow } from "../../types";
import Icon from "../../components/Icon";

type SpecsEditorProps = {
  assetId: string;
  initialSpecs: SpecRow[];
};

const MONO: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
};

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

type Draft = { label: string; value: string; spec_group: string };

const BLANK: Draft = { label: "", value: "", spec_group: "" };

export default function SpecsEditor({ assetId, initialSpecs }: SpecsEditorProps) {
  const [specs, setSpecs] = useState<SpecRow[]>(initialSpecs);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(BLANK);
  const [saving, setSaving] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<Draft>(BLANK);
  const [addSaving, setAddSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit(s: SpecRow) {
    setEditingId(s.id);
    setEditDraft({ label: s.label, value: s.value, spec_group: s.spec_group ?? "" });
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(BLANK);
  }

  async function saveEdit(specId: string) {
    setSaving(specId);
    setError(null);
    try {
      await api.updateSpec(assetId, specId, {
        label: editDraft.label,
        value: editDraft.value,
        spec_group: editDraft.spec_group || null,
      });
      setSpecs((prev) =>
        prev.map((s) =>
          s.id === specId
            ? { ...s, label: editDraft.label, value: editDraft.value, spec_group: editDraft.spec_group || null }
            : s
        )
      );
      setEditingId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  async function deleteSpec(specId: string) {
    setDeletingId(specId);
    setError(null);
    try {
      await api.deleteSpec(assetId, specId);
      setSpecs((prev) => prev.filter((s) => s.id !== specId));
      if (editingId === specId) setEditingId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  async function addSpec() {
    if (!addDraft.label.trim()) return;
    setAddSaving(true);
    setError(null);
    try {
      const res = await api.createSpec(assetId, {
        label: addDraft.label.trim(),
        value: addDraft.value,
        spec_group: addDraft.spec_group || null,
      });
      const newSpec: SpecRow = {
        id: res.id,
        asset_id: res.asset_id,
        label: addDraft.label.trim(),
        value: addDraft.value,
        sort_order: specs.length,
        spec_group: addDraft.spec_group || null,
      };
      setSpecs((prev) => [...prev, newSpec]);
      setAddDraft(BLANK);
      setAdding(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddSaving(false);
    }
  }

  return (
    <div className="field-group" style={{ marginBottom: 16 }}>
      <div className="field-group-head">
        <span style={EYEBROW}>Specs</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...MONO, color: "var(--fg-muted)" }}>{specs.length}</span>
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => { setAdding(true); setError(null); }}
            style={{ fontSize: 12 }}
          >
            <Icon name="plus" size={12} /> Add
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 8, fontSize: 12, ...MONO, color: "#e58a6d" }}>{error}</div>
      )}

      {specs.length > 0 && (
        <table className="table" style={{ marginBottom: adding ? 12 : 0 }}>
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Label</th>
              <th style={{ width: "35%" }}>Value</th>
              <th style={{ width: "20%" }}>Group</th>
              <th style={{ width: "15%" }} />
            </tr>
          </thead>
          <tbody>
            {specs.map((s) =>
              editingId === s.id ? (
                <tr key={s.id} style={{ cursor: "default", background: "var(--surface-raised)" }}>
                  <td>
                    <input
                      className="input"
                      value={editDraft.label}
                      onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))}
                      style={{ fontSize: 13 }}
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      value={editDraft.value}
                      onChange={(e) => setEditDraft((d) => ({ ...d, value: e.target.value }))}
                      style={{ fontSize: 13 }}
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      value={editDraft.spec_group}
                      onChange={(e) => setEditDraft((d) => ({ ...d, spec_group: e.target.value }))}
                      placeholder="optional"
                      style={{ fontSize: 13 }}
                    />
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => void saveEdit(s.id)}
                        disabled={saving === s.id}
                        style={{ fontSize: 12 }}
                      >
                        {saving === s.id ? "…" : "Save"}
                      </button>
                      <button
                        className="btn btn-quiet btn-sm"
                        onClick={cancelEdit}
                        style={{ fontSize: 12 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={s.id} style={{ cursor: "default" }}>
                  <td style={{ fontSize: 13, fontWeight: 520 }}>{s.label}</td>
                  <td style={{ fontSize: 13 }}>{s.value || "—"}</td>
                  <td style={{ ...MONO, fontSize: 12, color: "var(--fg-muted)" }}>
                    {s.spec_group ?? "—"}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        className="btn btn-quiet btn-sm"
                        onClick={() => startEdit(s)}
                        style={{ fontSize: 12 }}
                      >
                        <Icon name="pencil" size={12} />
                      </button>
                      <button
                        className="btn btn-quiet btn-sm"
                        onClick={() => void deleteSpec(s.id)}
                        disabled={deletingId === s.id}
                        style={{ fontSize: 12, color: "var(--fg-muted)" }}
                      >
                        {deletingId === s.id ? "…" : <Icon name="trash" size={12} />}
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      )}

      {adding && (
        <div
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r-sm)",
            padding: "12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr auto",
            gap: 8,
            alignItems: "end",
          }}
        >
          <div>
            <div style={{ ...EYEBROW, marginBottom: 4 }}>Label *</div>
            <input
              className="input"
              value={addDraft.label}
              onChange={(e) => setAddDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="e.g. Weight"
              autoFocus
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <div style={{ ...EYEBROW, marginBottom: 4 }}>Value</div>
            <input
              className="input"
              value={addDraft.value}
              onChange={(e) => setAddDraft((d) => ({ ...d, value: e.target.value }))}
              placeholder="e.g. 2.3 lbs"
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <div style={{ ...EYEBROW, marginBottom: 4 }}>Group</div>
            <input
              className="input"
              value={addDraft.spec_group}
              onChange={(e) => setAddDraft((d) => ({ ...d, spec_group: e.target.value }))}
              placeholder="optional"
              style={{ fontSize: 13 }}
            />
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void addSpec()}
              disabled={addSaving || !addDraft.label.trim()}
              style={{ fontSize: 12 }}
            >
              {addSaving ? "Adding…" : "Add"}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => { setAdding(false); setAddDraft(BLANK); }}
              style={{ fontSize: 12 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {specs.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: "var(--fg-subtle)", fontStyle: "italic" }}>
          No specs yet.
        </div>
      )}
    </div>
  );
}
